import {useMemo} from 'react';
import clsx from 'clsx';
import {DATA_TEST_IDS} from '~/lib/const';

const tshirtSizeOrder = [
  'XS',
  'XS-S',
  'XS/S',
  'XS S',
  'S',
  'S-M',
  'S/M',
  'S M',
  'M',
  'M-L',
  'M/L',
  'M L',
  'L',
  'L-XL',
  'L/XL',
  'L XL',
  'XL',
  'XL-2XL',
  'XL/2XL',
  'XL 2XL',
  '2XL',
  '2XL-3XL',
  '2XL/3XL',
  '2XL 3XL',
  '3XL',
  '3XL-4XL',
  '3XL/4XL',
  '3XL 4XL',
  '4XL',
  'XL-XXL',
  'XL/XXL',
  'XL XXL',
  'XXL',
  'XXL-XXXL',
  'XXL/XXXL',
  'XXL XXXL',
  'XXXL',
  'XXXL-XXXXL',
  'XXXL/XXXXL',
  'XXXL XXXXL',
  'XXXXL',
  'XXXXL-XXXXXL',
  'XXXXL/XXXXXL',
  'XXXXL XXXXXL',
  'XXXXXL',
];

const romanNumeralOrder = [
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
];

export function ProductOptionsSwatch({
  values,
  availableValues,
  name,
  activeValue,
  error,
  handleChange,
  sortDisabled,
}: {
  values: string[];
  availableValues: string[];
  name: string;
  activeValue: string | null;
  error?: boolean;
  handleChange: (name: string, value: string) => void;
  sortDisabled?: boolean;
}) {
  const orderedValues = useMemo(() => {
    if (sortDisabled) return values;

    return values.sort((a, b) => {
      const isACurrency = a.startsWith('$');
      const isBCurrency = b.startsWith('$');

      if (isACurrency && isBCurrency) {
        return parseFloat(a.slice(1).trim()) - parseFloat(b.slice(1).trim());
      }

      const isANumber = !isNaN(parseFloat(a));
      const isBNumber = !isNaN(parseFloat(b));

      if (isANumber && isBNumber) {
        return parseFloat(a) - parseFloat(b);
      }

      const aTShirtOrder = tshirtSizeOrder.indexOf(a.toUpperCase());
      const bTShirtOrder = tshirtSizeOrder.indexOf(b.toUpperCase());

      if (aTShirtOrder !== -1 && bTShirtOrder !== -1) {
        return aTShirtOrder - bTShirtOrder;
      }

      const aRomanNumeralOrder = romanNumeralOrder.indexOf(a.toUpperCase());
      const bRomanNumeralOrder = romanNumeralOrder.indexOf(b.toUpperCase());

      if (aRomanNumeralOrder !== -1 && bRomanNumeralOrder !== -1) {
        return aRomanNumeralOrder - bRomanNumeralOrder;
      }

      return a.localeCompare(b);
    });
  }, [values, sortDisabled]);

  return (
    <div data-testid={DATA_TEST_IDS.OPTIONS_COLOR} className="relative">
      <div className={'flex flex-wrap gap-2'}>
        {orderedValues.map((value) => {
          const checked =
            activeValue?.toLowerCase() ===
            encodeURIComponent(value.toLowerCase());
          const id = `option-${name}-${value}`;
          const isAvailable = availableValues.includes(value);

          return (
            <label
              key={id}
              htmlFor={id}
              className={clsx(
                'border-2 border-solid bg-white rounded-lg overflow-hidden cursor-pointer',
                checked && 'border-primary',
              )}
            >
              <input
                data-testid={DATA_TEST_IDS.INPUT(name)}
                className="sr-only"
                type="radio"
                id={id}
                name={`option[${name}]`}
                value={value}
                checked={checked}
                onChange={() => handleChange(name, value)}
              />
              <div
                data-testid={DATA_TEST_IDS.CHECKBOX('checked')}
                className={clsx(
                  'leading-none transition-all duration-200 px-4 py-3',
                  !isAvailable && 'line-through text-gray-600',
                )}
              >
                {value}
              </div>
            </label>
          );
        })}
      </div>
      {error && (
        <div
          data-testid={DATA_TEST_IDS.ERROR}
          className="text-red-500 text-xs mt-1 absolute whitespace-nowrap"
        >
          This is a required field.
        </div>
      )}
    </div>
  );
}
